import {
  CheckCheck,
  CircleAlert,
  CircleHelp,
  Eye,
  Globe2,
  MessageSquareText,
  Phone,
  Star,
} from 'lucide-react'

import type { Conversation } from '../conversations-model'
import type { InteractionOperationalState } from '../interaction-operational-contracts'
import { conversationAttention } from '../conversation-attention'
import { conversationOutcome } from '../handoff-presentation'
import styles from '../ConversationsPage.module.css'
import { HandoffBadge, IntentBadge } from './IntentBadge'

function timeLabel(timestamp: number) {
  const date = new Date(timestamp)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  return new Intl.DateTimeFormat(
    undefined,
    sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric' }
  ).format(timestamp)
}

function channelIcon(channel: Conversation['channel']) {
  if (channel === 'SMS')
    return <MessageSquareText size={13} aria-hidden="true" />
  if (channel === 'Voice') return <Phone size={13} aria-hidden="true" />
  if (channel === 'Web Widget') return <Globe2 size={13} aria-hidden="true" />
  return <CircleHelp size={13} aria-hidden="true" />
}

export function ConversationRow({
  conversation,
  selected,
  onSelect,
  operationalState,
}: {
  conversation: Conversation
  selected: boolean
  onSelect: () => void
  operationalState?: InteractionOperationalState
}) {
  const attention = conversationAttention(operationalState)
  const outcome = conversation.latestFacts
    ? conversationOutcome(conversation.latestFacts, {
        atMs: conversation.latestInteractionAtMs ?? conversation.timestamp,
      })
    : null
  const initials = conversation.details.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <li>
      <button
        className={`${styles.conversationRow} ${selected ? styles.selectedRow : ''} ${operationalState?.unreadForCaller ? styles.unreadRow : ''}`}
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials || 'P'}
        </span>
        <span className={styles.rowBody}>
          <span className={styles.rowHeading}>
            <strong>
              {operationalState?.unreadForCaller ? (
                <span className={styles.unreadDot} aria-label="Unread" />
              ) : null}
              {conversation.details.displayName}
            </strong>
            {operationalState?.starred ? (
              <Star
                className={styles.starredIcon}
                size={13}
                fill="currentColor"
                aria-label="Starred"
              />
            ) : null}
            {attention === 'NONE' &&
            operationalState?.reviewStatus === 'REVIEWED' ? (
              <CheckCheck
                className={styles.reviewedIcon}
                size={14}
                role="img"
                aria-label="Reviewed"
              />
            ) : null}
            <time
              dateTime={new Date(conversation.timestamp).toISOString()}
              title={new Intl.DateTimeFormat(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(conversation.timestamp)}
            >
              {timeLabel(conversation.timestamp)}
            </time>
          </span>
          <span className={styles.rowMeta}>
            <span className={styles.channelBadge}>
              {channelIcon(conversation.channel)}
              {conversation.channel}
            </span>
            <span className={styles.rowPhone}>
              {conversation.details.phone ?? 'Phone unavailable'}
            </span>
          </span>
          {attention === 'NEEDS_ACTION' ||
          attention === 'NEEDS_REVIEW' ||
          conversation.latestIntent ? (
            <span className={styles.requestBadges}>
              {conversation.latestIntent ? (
                <IntentBadge intent={conversation.latestIntent} prominent />
              ) : null}
              {attention === 'NEEDS_ACTION' ? (
                <span className={styles.actionBadge}>
                  <CircleAlert size={11} strokeWidth={2.5} aria-hidden="true" />
                  Needs action
                </span>
              ) : attention === 'NEEDS_REVIEW' ? (
                <span className={styles.reviewBadge}>
                  <Eye size={11} strokeWidth={2.5} aria-hidden="true" />
                  Needs review
                </span>
              ) : null}
            </span>
          ) : null}
          {outcome ? (
            <span className={styles.rowHandoff}>
              {outcome.label ? (
                <HandoffBadge presentation={outcome} label={outcome.label} />
              ) : null}
              <span className={styles.rowHandoffNote}>
                {outcome.description}
              </span>
            </span>
          ) : null}
          <span className={styles.preview}>{conversation.preview}</span>
        </span>
      </button>
    </li>
  )
}
